import React, { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { BookOpen, CheckCircle2, XCircle, Users, Calendar, Search, MinusCircle } from 'lucide-react';

// Read-only view of the Teacher's Diary, recorded when a lesson plan is marked
// taught (POST /teacher/lesson-plans/{id}/mark-taught). Shared by the HOD
// dashboard review modal and the Admin Lesson Plan Management page so both
// roles see exactly the same thing.
//
// Expects a lesson plan / summary object carrying: status, taught_at, outcome1-3,
// objectivesAchieved, reflections.teacher, studentFeedback.

const OBJECTIVES = [
    { key: 'outcome1', label: 'Learning Objective 1' },
    { key: 'outcome2', label: 'Learning Objective 2' },
    { key: 'outcome3', label: 'Learning Objective 3' },
];

const REFLECTION_FIELDS = [
    { key: 'whatWorked', label: 'What worked well' },
    { key: 'extraSupport', label: 'Students needing extra support' },
    { key: 'timingRealistic', label: 'Timing assessment' },
    { key: 'classroomArrangements', label: 'Classroom arrangements' },
    { key: 'modificationsNext', label: 'Modifications for next time', wide: true },
];

const hasText = (v) => typeof v === 'string' && v.replace(/<[^>]+>/g, '').trim() !== '';

const RichOrPlain = ({ content }) => {
    if (typeof content === 'string' && content.includes('<')) {
        return (
            <div
                className="prose prose-sm max-w-none text-gray-800"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
            />
        );
    }
    return <p className="text-sm text-gray-800 whitespace-pre-wrap">{content}</p>;
};

const formatTaughtDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export const hasTeacherDiary = (plan) => {
    if (!plan) return false;
    const reflections = plan.reflections?.teacher || {};
    return (
        Object.values(reflections).some(hasText) ||
        Object.keys(plan.objectivesAchieved || {}).length > 0 ||
        (plan.studentFeedback || []).length > 0
    );
};

const TeacherDiaryPanel = ({ plan, className = '' }) => {
    const [query, setQuery] = useState('');
    const [onlyWithNotes, setOnlyWithNotes] = useState(false);

    const reflections = plan?.reflections?.teacher || {};
    const achieved = plan?.objectivesAchieved || {};
    const students = useMemo(() => (Array.isArray(plan?.studentFeedback) ? plan.studentFeedback : []), [plan]);

    const objectives = OBJECTIVES.filter(o => hasText(plan?.[o.key]) || o.key in achieved);
    const achievedCount = objectives.filter(o => achieved[o.key]).length;
    const withNotes = students.filter(s => hasText(s.feedback)).length;

    const visibleStudents = useMemo(() => {
        const q = query.trim().toLowerCase();
        return students.filter(s => {
            if (onlyWithNotes && !hasText(s.feedback)) return false;
            if (!q) return true;
            return (s.student_name || '').toLowerCase().includes(q) || (s.feedback || '').toLowerCase().includes(q);
        });
    }, [students, query, onlyWithNotes]);

    const taughtOn = formatTaughtDate(plan?.taught_at);
    const isTaught = plan?.status === 'taught';

    return (
        <section className={`rounded-xl border border-orange-200 bg-white overflow-hidden ${className}`}
                 aria-label="Teacher's Diary">
            <header className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-[#F7FAFF] border-b border-orange-100">
                <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-[#D67C2C] flex items-center justify-center">
                        <BookOpen className="w-4 h-4 text-white" />
                    </span>
                    <h3 className="text-base font-semibold text-gray-900">Teacher's Diary</h3>
                </div>
                {taughtOn && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2.5 py-1">
                        <Calendar className="w-3.5 h-3.5" /> Taught on {taughtOn}
                    </span>
                )}
            </header>

            {!hasTeacherDiary(plan) ? (
                <p className="px-4 py-6 text-sm text-gray-500 italic">
                    {isTaught
                        ? 'This lesson was marked as taught without a diary entry.'
                        : 'No diary entry yet. The teacher fills it in when the lesson is marked as taught.'}
                </p>
            ) : (
                <div className="p-4 space-y-6">
                    {/* Learning objectives */}
                    {objectives.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-gray-700">Learning objectives</h4>
                                <span className="text-xs text-gray-500">
                                    {achievedCount} of {objectives.length} achieved
                                </span>
                            </div>
                            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                                {objectives.map(o => {
                                    const ok = !!achieved[o.key];
                                    return (
                                        <li key={o.key} className="flex items-start gap-3 px-3 py-2.5">
                                            {ok
                                                ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                                                : <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-medium text-gray-500">{o.label}</p>
                                                <p className="text-sm text-gray-900">{plan[o.key] || 'Not specified'}</p>
                                            </div>
                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                                                {ok ? 'Achieved' : 'Not achieved'}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {/* Teacher reflection */}
                    {REFLECTION_FIELDS.some(f => hasText(reflections[f.key])) && (
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Teacher reflection</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {REFLECTION_FIELDS.filter(f => hasText(reflections[f.key])).map(f => (
                                    <div key={f.key}
                                         className={`rounded-lg bg-orange-50 border border-orange-100 p-3 ${f.wide ? 'md:col-span-2' : ''}`}>
                                        <p className="text-xs font-semibold text-orange-800 mb-1">{f.label}</p>
                                        <RichOrPlain content={reflections[f.key]} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Student feedback — every student in the class roster */}
                    {students.length > 0 && (
                        <div>
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                                    <Users className="w-4 h-4 text-gray-500" />
                                    Student feedback
                                    <span className="font-normal text-gray-500">
                                        ({students.length} students · {withNotes} with notes)
                                    </span>
                                </h4>
                                <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                        <input type="checkbox" checked={onlyWithNotes}
                                               onChange={e => setOnlyWithNotes(e.target.checked)} />
                                        Only with notes
                                    </label>
                                    {students.length > 8 && (
                                        <div className="relative">
                                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                                            <input
                                                type="text"
                                                value={query}
                                                onChange={e => setQuery(e.target.value)}
                                                placeholder="Search students"
                                                className="pl-7 pr-2 py-1 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-80 overflow-y-auto">
                                {visibleStudents.length === 0 ? (
                                    <p className="px-3 py-4 text-sm text-gray-500 italic">No students match.</p>
                                ) : visibleStudents.map((s, i) => (
                                    <div key={s.student_id || i} className="flex items-start gap-3 px-3 py-2.5">
                                        <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center shrink-0">
                                            {(s.student_name || '?').trim().charAt(0).toUpperCase()}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-900">{s.student_name || 'Unnamed student'}</p>
                                            {hasText(s.feedback)
                                                ? <p className="text-sm text-gray-700 whitespace-pre-wrap">{s.feedback}</p>
                                                : <p className="text-xs text-gray-400 italic flex items-center gap-1"><MinusCircle className="w-3 h-3" /> No note</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default TeacherDiaryPanel;
